import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import BlogCard from '../components/blog/BlogCard';
import { AnimatedText } from '../components/AnimatedText';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import blogPostsData from '../data/blog-posts.json';
import type { BlogPost } from '../types/blog';

const blogPosts = (blogPostsData as BlogPost[]).sort((a, b) =>
  new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
);

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [postsToShow, setPostsToShow] = useState(12);
  const sectionRef = useRef<HTMLDivElement>(null);

  // GSAP Animations - Using global GSAP from CDN (same as OurTeamPage)
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Parallax effect for left images - move further left on scroll
      gsap.utils.toArray('.parallax-image-left').forEach((img: any) => {
        const speed = parseFloat(img.dataset.speed) || -0.3;
        gsap.to(img, {
          x: () => speed * 300,
          ease: 'none',
          scrollTrigger: {
            trigger: '.hero-parallax',
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          }
        });
      });

      // Parallax effect for right images - move further right on scroll
      gsap.utils.toArray('.parallax-image-right').forEach((img: any) => {
        const speed = parseFloat(img.dataset.speed) || 0.3;
        gsap.to(img, {
          x: () => speed * 300,
          ease: 'none',
          scrollTrigger: {
            trigger: '.hero-parallax',
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          }
        });
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    blogPosts.forEach(post => {
      post.categories.forEach(cat => cats.add(cat));
    });
    return ['All', ...Array.from(cats).sort()];
  }, []);

  // Filter posts
  const latestPost = blogPosts[0];
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      // Don't show latest post in the grid if we are on the default view
      if (post.id === latestPost?.id && searchQuery === '' && selectedCategory === 'All') {
        return false;
      }
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || post.categories.includes(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, latestPost]);

  // Get posts to display
  const displayedPosts = filteredPosts.slice(0, postsToShow);
  const hasMore = filteredPosts.length > postsToShow;

  const handleLoadMore = () => {
    setPostsToShow(prev => prev + 12);
  };

  return (
    <div ref={sectionRef} className="blog-page min-h-screen bg-white transition-colors duration-300">
      {/* Hero Section - Latest Blog Post */}
      <section className="relative min-h-[80vh] bg-[#293039] pt-32 pb-20 px-6 lg:px-12 flex flex-col justify-center">
        {/* Title */}
        <div className="max-w-[1600px] mx-auto w-full mb-12">
          <div className="text-4xl md:text-5xl lg:text-6xl font-serif text-white opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Insights from <span className="text-[#d68800]">Our News Desk</span>
          </div>
          <p className="mt-4 text-lg text-white/60 max-w-2xl opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Insightful articles written by our therapists, covering mental health tips, coping strategies, and personal growth.
          </p>
        </div>

        {/* Latest Post Card - Horizontal */}
        {latestPost && (
          <Link to={`/blog/${latestPost.slug}`} className="max-w-[1600px] mx-auto w-full group opacity-0 animate-fade-in-up" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
            <div className="flex flex-col lg:flex-row bg-[#1a1f25] rounded-[2rem] overflow-hidden shadow-2xl border border-white/5 transition-transform duration-500 hover:-translate-y-2 hover:shadow-[#d68800]/20">
              {/* Image side */}
              <div className="w-full lg:w-1/2 relative h-[300px] lg:h-[500px] overflow-hidden">
                <img 
                  src={latestPost.featuredImage} 
                  alt={latestPost.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-6 left-6 flex gap-2">
                  <span className="px-4 py-1.5 bg-[#d68800] text-white text-sm font-bold rounded-full uppercase tracking-wider shadow-lg">
                    Latest Post
                  </span>
                  {latestPost.categories[0] && (
                    <span className="px-4 py-1.5 bg-white/20 backdrop-blur-md text-white text-sm font-bold rounded-full uppercase tracking-wider shadow-lg">
                      {latestPost.categories[0]}
                    </span>
                  )}
                </div>
              </div>

              {/* Content side */}
              <div className="w-full lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-white/50 text-sm mb-6">
                  <span>{new Date(latestPost.publishedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  <span>•</span>
                  <span>{latestPost.readTime} min read</span>
                </div>
                
                <h2 className="text-3xl lg:text-5xl font-serif text-white mb-6 group-hover:text-[#d68800] transition-colors duration-300 leading-tight">
                  {latestPost.title}
                </h2>
                
                <p className="text-lg text-white/70 leading-relaxed mb-8 line-clamp-3">
                  {latestPost.excerpt}
                </p>

                <div className="flex items-center gap-3 text-white font-medium mt-auto group-hover:text-[#d68800] transition-colors">
                  Read Article <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </div>
          </Link>
        )}
      </section>

      {/* Content Section */}
      <div id="blog-content" className="w-full max-w-[1600px] mx-auto px-6 lg:px-12 py-12 md:py-16 md:-mt-10 relative z-30">

        {/* Search and Filter */}
        <div className="blog-controls bg-[#293039]/80 backdrop-blur-md p-6 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-white/10 mb-16 flex flex-col md:flex-row gap-6 sticky top-24 z-40 transition-all duration-300">
          {/* Search Bar */}
          <div className="flex-1 relative group">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-8 py-4 bg-[#1a1f25] hover:bg-black/40 focus:bg-black/40 rounded-2xl border border-transparent focus:border-[#d68800]/50 outline-none transition-all duration-300 text-white placeholder:text-white/40 font-medium shadow-inner focus:shadow-lg focus:shadow-[#d68800]/5"
              style={{ fontFamily: 'Sofia Pro, sans-serif', fontSize: '1.1rem' }}
            />
            <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-[#d68800] to-transparent scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500" />
          </div>

          {/* Category Filter */}
          <div className="md:w-72 relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-8 py-4 bg-[#1a1f25] hover:bg-black/40 focus:bg-black/40 rounded-2xl border border-transparent focus:border-[#d68800]/50 outline-none transition-all duration-300 text-white cursor-pointer font-medium appearance-none shadow-inner focus:shadow-lg focus:shadow-[#d68800]/5"
              style={{ fontFamily: 'Sofia Pro, sans-serif', fontSize: '1.1rem' }}
            >
              {categories.map(category => (
                <option key={category} value={category} className="bg-[#293039] text-white">
                  {category}
                </option>
              ))}
            </select>
            <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-8 px-2">
          <p className="text-gray-500 font-medium" style={{ fontFamily: 'Sofia Pro, sans-serif' }}>
            Showing {displayedPosts.length} of {filteredPosts.length} articles
          </p>
        </div>

        {/* Blog Grid */}
        <div className="pb-24">
          {filteredPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {displayedPosts.map((post, index) => (
                  <div key={post.id} className="opacity-0 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}>
                    <BlogCard {...post} />
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-20">
                  <button
                    onClick={handleLoadMore}
                    className="inline-flex items-center gap-4 bg-[#293039] text-white pl-10 pr-3 py-3 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 hover:bg-[#d68800] shadow-xl hover:shadow-[#d68800]/30 group"
                  >
                    Load More
                    <div className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                      <ArrowUpRight className="w-6 h-6" />
                    </div>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-32 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
              <div className="mb-6 text-gray-300">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mx-auto"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No articles found</h3>
              <p className="text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                className="mt-6 text-[#d68800] font-semibold hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Newsletter Signup Section */}
      <section className="newsletter-section py-20 bg-[#293039] relative z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">Subscribe to Our Newsletter</h2>
          <p className="text-white/70 mb-10">Stay updated with our latest insights, tips, and resources.</p>
          <div className="bg-white/5 p-2 rounded-3xl shadow-xl overflow-hidden backdrop-blur-sm flex justify-center">
            <iframe width="540" height="305" src="https://3ce33466.sibforms.com/serve/MUIFAHHCYDzuSichwdQ5qAygJbnX-LsOsV33RG7t5A26xeFSY_IZlKmK95cMaQHzwo_Fd_COUmYwdYG0_sy3lbmYwTHohKJYcOAY2SMJU1JjFCH9YJwqI83f0R9GVeT8eesHqPTw9Xu9IIPJSRhRIRC5MaqOwtZaoOXyMV01d5UneC5ueOj-UgscsHdrLQxNhQEsuKT40a6RwT3f" frameBorder="0" scrolling="auto" allowFullScreen style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }} className="rounded-2xl bg-white"></iframe>
          </div>
        </div>
      </section>

      <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                .parallax-image-left, .parallax-image-right {
                    will-change: transform;
                }
            `}</style>
    </div>
  );
}
